import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import type { PlaybookLabel, ContentLabel } from '@/types/Label.types'

export const UNTAGGED_FILTER = '__untagged__' as const

export const playbookLabelKeys = {
	all: (playbookId: string) => ['playbook-labels', playbookId] as const,
	counts: (playbookId: string, blockType?: string) =>
		['playbook-labels', playbookId, 'counts', blockType] as const,
}

interface LabelableItem {
	id: string
	labels?: ContentLabel[]
}

// --- Standalone filter/count functions (exported for testing) ---

export function filterContent<T extends LabelableItem>(
	items: T[],
	filter: string | null,
): T[] {
	if (!filter) return items

	if (filter === UNTAGGED_FILTER) {
		return items.filter((item) => !item.labels || item.labels.length === 0)
	}

	return items.filter((item) =>
		item.labels?.some((label) => label.id === filter),
	)
}

export function filterContentMulti<T extends LabelableItem>(
	items: T[],
	filterIds: string[],
): T[] {
	if (filterIds.length === 0) return items

	// AND logic: item must have ALL selected labels
	return items.filter((item) =>
		filterIds.every((filterId) =>
			item.labels?.some((label) => label.id === filterId),
		),
	)
}

export function computeCounts<T extends LabelableItem>(items: T[]) {
	const counts: Record<string, number> = {}
	let untaggedCount = 0

	for (const item of items) {
		if (!item.labels || item.labels.length === 0) {
			untaggedCount++
		} else {
			for (const label of item.labels) {
				counts[label.id] = (counts[label.id] || 0) + 1
			}
		}
	}

	return { counts, untaggedCount, totalCount: items.length }
}

/**
 * Prefetch playbook labels into the React Query cache.
 * Call this when you know a playbook will be opened soon
 * (e.g., on hover or navigation).
 */
export function prefetchPlaybookLabels(
	queryClient: { prefetchQuery: Function },
	playbookId: string,
) {
	queryClient.prefetchQuery({
		queryKey: playbookLabelKeys.all(playbookId),
		queryFn: async () => {
			const res = await secureFetch(`/api/playbooks/${playbookId}/labels`)
			if (!res.ok) throw new Error('Failed to fetch labels')
			const body = await res.json()
			return body.labels as PlaybookLabel[]
		},
		staleTime: 30_000,
	})
}

// --- React hook ---

export function usePlaybookLabels(playbookId: string | undefined) {
	const queryClient = useQueryClient()

	// --- Filter state ---
	const [activeFilter, setActiveFilter] = useState<string | null>(null)
	const [activeFilterIds, setActiveFilterIds] = useState<string[]>([])

	// --- Fetch labels ---
	const {
		data: labels = [],
		isLoading,
		error,
	} = useQuery({
		queryKey: playbookLabelKeys.all(playbookId!),
		queryFn: async () => {
			const res = await secureFetch(`/api/playbooks/${playbookId}/labels`)
			if (!res.ok) throw new Error('Failed to fetch labels')
			const body = await res.json()
			return body.labels as PlaybookLabel[]
		},
		enabled: !!playbookId,
	})

	// --- Mutations ---
	const invalidate = useCallback(
		() => queryClient.invalidateQueries({ queryKey: playbookLabelKeys.all(playbookId!) }),
		[queryClient, playbookId],
	)

	const createLabel = useMutation({
		mutationFn: async (data: { name: string; color: string }) => {
			const res = await secureJsonFetch(`/api/playbooks/${playbookId}/labels`, {
				method: 'POST',
				body: data,
			})
			if (!res.ok) {
				const err = await res.json()
				throw new Error(err.error || 'Failed to create label')
			}
			return (await res.json()).label as PlaybookLabel
		},
		onSuccess: invalidate,
	})

	const updateLabel = useMutation({
		mutationFn: async ({ id, ...data }: { id: string; name?: string; color?: string }) => {
			const res = await secureJsonFetch(`/api/playbooks/${playbookId}/labels/${id}`, {
				method: 'PUT',
				body: data,
			})
			if (!res.ok) throw new Error('Failed to update label')
			return (await res.json()).label as PlaybookLabel
		},
		onSuccess: invalidate,
	})

	const deleteLabel = useMutation({
		mutationFn: async (labelId: string) => {
			const res = await secureFetch(`/api/playbooks/${playbookId}/labels/${labelId}`, {
				method: 'DELETE',
			})
			if (!res.ok) throw new Error('Failed to delete label')
		},
		onSuccess: () => {
			if (activeFilter && !labels.find((l) => l.id === activeFilter)) {
				setActiveFilter(null)
			}
			invalidate()
		},
	})

	const reorderLabels = useMutation({
		mutationFn: async (labelIds: string[]) => {
			const res = await secureJsonFetch(`/api/playbooks/${playbookId}/labels/reorder`, {
				method: 'PUT',
				body: { label_ids: labelIds },
			})
			if (!res.ok) throw new Error('Failed to reorder labels')
		},
		onSuccess: invalidate,
	})

	const setBlockLabels = useMutation({
		mutationFn: async ({ blockId, labelIds }: { blockId: string; labelIds: string[] }) => {
			const res = await secureJsonFetch(`/api/blocks/${blockId}/labels`, {
				method: 'PUT',
				body: { label_ids: labelIds },
			})
			if (!res.ok) throw new Error('Failed to set block labels')
		},
	})

	// --- Has any tagged items (for showing "Untagged" pill) ---
	const hasTaggedItems = useCallback(
		<T extends LabelableItem>(items: T[]) =>
			items.some((item) => item.labels && item.labels.length > 0),
		[],
	)

	return {
		// Data
		labels,
		isLoading,
		error,

		// Filter state
		activeFilter,
		setActiveFilter,
		activeFilterIds,
		setActiveFilterIds,

		// Filtering functions
		filterContent,
		filterContentMulti,

		// Computed helpers
		computeCounts,
		hasTaggedItems,

		// Mutations
		createLabel: createLabel.mutateAsync,
		updateLabel: updateLabel.mutateAsync,
		deleteLabel: deleteLabel.mutateAsync,
		reorderLabels: reorderLabels.mutateAsync,
		setBlockLabels: setBlockLabels.mutateAsync,
		isCreating: createLabel.isPending,
		isUpdating: updateLabel.isPending,
	}
}
